import Vue from 'vue';
import Vuex from 'vuex';

import axios, { AxiosResponse } from 'axios';
import _ from 'lodash';
import moment from 'moment-timezone';

const BASE_URL = process.env.API_BASE_URL ?? 'http://localhost:8000/';

const client = axios.create({
  baseURL: BASE_URL,
  headers: {
    Authorization: `Bearer ${window.localStorage.getItem('jwtAccess')}`,
  },
});

Vue.use(Vuex);

export interface Credentials {
  username: string;
  password: string;
}

interface JWTResponse {
  access: string;
  refresh: string;
  display_name: string;
  own_depots: number[];
}

interface JWTRefreshResponse {
  access: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export type TransactionType = 'C' | 'D' | '3';

interface Incident {
  readonly incident_type: number;
  readonly by: number;
  readonly by_name: string;
  readonly at: string;
}

interface BaseConsignment {
  readonly tracking_id: number;
  readonly consignment_number: string;
  readonly customer_reference: string;
  readonly transaction: TransactionType;
  readonly account_code: string;
  readonly account_name: string;
  readonly date_entered: string;
  readonly time_entered: string;
  readonly due_date: string;
  readonly due_time: string;
  readonly consignment_status: string;
  readonly delivery_depot_number: number;
  // Extra data
  readonly original_due_date: string;
  readonly original_due_time: string;
  readonly delivery_address_contact: string;
  readonly delivery_address_company: string;
  readonly delivery_address_country: string;
  readonly delivery_address_postcode: string;
  readonly incident: Incident | null;
  readonly incident_type_url: string;
  readonly delivery_completed_date: string;
  readonly delivery_completed_time: string;
  readonly delivery_service: string;
  readonly delivery_surcharge: string;
}

interface UnreviewedConsignment extends BaseConsignment {
  readonly reviewed: false;
  readonly review_url: string;
}

interface ReviewedConsignment extends BaseConsignment {
  readonly reviewed: true;
  readonly unreview_url: string;
  readonly reviewed_by: string;
  readonly reviewed_at: string;
}

export type Consignment = UnreviewedConsignment | ReviewedConsignment;

export interface ConsignmentStatus {
  consignment_status_id: number;
  consignment_status: string;
}

export interface Account {
  account_code: string;
  account_name: string;
}

export interface Service {
  service_code: string;
  surcharge_code: string;
  service: string;
  surcharge: string;
}

export interface Depot {
  depot_number: number;
  depot_name: string;
}

export interface IncidentType {
  id: number;
  name: string;
}

export interface ConsignmentFilters {
  status?: number[];
  overdueStatus?: string[];
  transaction: TransactionType[];
  account: string[];
  ownDepot: number[];
  service: string[];
  deliveryDepot: number[];
  enteredFrom?: moment.Moment;
  enteredTo?: moment.Moment;
  dueFrom?: moment.Moment;
  dueTo?: moment.Moment;
}

interface FilterSpec {
  generic?: string;
  ordering?: string[];
  filters?: ConsignmentFilters;
}

interface ReviewResponse {
  reviewed_by: string;
  reviewed_at: string;
  unreview_url: string;
}

interface UnreviewResponse {
  review_url: string;
}

enum AUTH_FAILURE_TYPE {
  UNAUTHORISED,
  FORBIDDEN,
};

function momentToParam(m?: moment.Moment) {
  if (m?.isValid()) {
    return m.format('YYYY-MM-DD');
  } else {
    return undefined;
  }
}

function filterSpecToGetParams(filters?: FilterSpec) {
  return {
    status: filters?.filters?.status,
    overdueStatus: filters?.filters?.overdueStatus,
    transaction: filters?.filters?.transaction,
    account: filters?.filters?.account,
    ownDepot: filters?.filters?.ownDepot,
    service: filters?.filters?.service,
    deliveryDepot: filters?.filters?.deliveryDepot,
    enteredFrom: momentToParam(filters?.filters?.enteredFrom),
    enteredTo: momentToParam(filters?.filters?.enteredTo),
    dueFrom: momentToParam(filters?.filters?.dueFrom),
    dueTo: momentToParam(filters?.filters?.dueTo),
    search: filters?.generic,
    ordering: filters?.ordering?.join(','),
  };
}

const store = new Vuex.Store({
  state: {
    jwtAccess: window.localStorage.getItem('jwtAccess'),
    jwtRefresh: window.localStorage.getItem('jwtRefresh'),
    userDisplayName: window.localStorage.getItem('userDisplayName'),
    ownDepots: JSON.parse(window.localStorage.getItem('ownDepots') ?? '[]') as number[],
    authFailureHandler: (() => { /* Do nothing by default */
    }) as (state: AUTH_FAILURE_TYPE) => void,
    jwtRefreshPromise: null as Promise<AxiosResponse<JWTRefreshResponse>> | null,
  },
  mutations: {
    setJwt(state, response: JWTResponse) {
      state.jwtAccess = response.access;
      state.jwtRefresh = response.refresh;
      state.userDisplayName = response.display_name;
      state.ownDepots = response.own_depots;
      client.defaults.headers.Authorization = `Bearer ${response.access}`;
      window.localStorage.setItem('jwtAccess', response.access);
      window.localStorage.setItem('jwtRefresh', response.refresh);
      window.localStorage.setItem('userDisplayName', response.display_name);
      window.localStorage.setItem('ownDepots', JSON.stringify(response.own_depots));
    },
    clearJwt(state) {
      state.jwtAccess = null;
      state.jwtRefresh = null;
      delete client.defaults.headers.Authorization;
      window.localStorage.removeItem('jwtAccess');
      window.localStorage.removeItem('jwtRefresh');
      window.localStorage.removeItem('userDisplayName');
      window.localStorage.removeItem('ownDepots');
    },
    setJwtRefreshPromise(state, promise: Promise<AxiosResponse<JWTRefreshResponse>>) {
      state.jwtRefreshPromise = promise;
    },
    refreshJwt(state, accessToken: string) {
      state.jwtAccess = accessToken;
      client.defaults.headers.Authorization = `Bearer ${accessToken}`;
      state.jwtRefreshPromise = null;
    },
    setAuthFailureHandler(state, handler: (state: AUTH_FAILURE_TYPE) => void) {
      state.authFailureHandler = handler;
    },
  },
  getters: {
    isLoggedIn(state) {
      return state.jwtAccess !== null;
    },
    username(state): string | null {
      if (state.jwtAccess === null) {
        return null;
      } else {
        return state.userDisplayName;
      }
    },
  },
  actions: {
    async login(context, credentials: Credentials) {
      const response = await client.post<JWTResponse>('aaa/token/', credentials);
      context.commit('setJwt', response.data);
    },
    logout(context) {
      context.commit('clearJwt');
    },
    async refreshJWT(context) {
      if (context.state.jwtRefreshPromise === null) {
        const promise = client.post<JWTRefreshResponse>('aaa/token/refresh/', {
          refresh: context.state.jwtRefresh,
        });
        context.commit('setJwtRefreshPromise', promise);
        context.commit('refreshJwt', (await promise).data.access);
      } else {
        await context.state.jwtRefreshPromise;
      }
    },
  },
  modules: {
    consignments: {
      namespaced: true,
      state: {
        loading: true,
        consignments: [] as Consignment[],
        nextLink: null as string | null,
        prevLink: null as string | null,
        total: 0,
      },
      getters: {
        hasNext(state) {
          return state.nextLink !== null;
        },
        hasPrev(state) {
          return state.prevLink !== null;
        },
        total(state) {
          return state.total;
        },
        currentPage(state) {
          return state.consignments;
        },
      },
      mutations: {
        _startLoad(state) {
          state.loading = true;
        },
        _update(state, response: PaginatedResponse<Consignment>) {
          state.consignments = response.results;
          state.nextLink = response.next;
          state.prevLink = response.previous;
          state.total = response.count;
          state.loading = false;
        },
        _updateConsignment(state, consignment: Consignment) {
          const idx = state.consignments.findIndex((c: Consignment) => c.tracking_id === consignment.tracking_id);
          state.consignments.splice(idx, 1, consignment);
        },
      },
      actions: {
        load: _.debounce(async function(context, filters?: FilterSpec) {
          context.commit('_startLoad');

          const params: { [key: string]: string | string[] | number[] | undefined } = filterSpecToGetParams(filters);
          const response = await client.get<PaginatedResponse<Consignment>>('api/consignment/', {
            params,
          });
          context.commit('_update', response.data);
        }, 1000),
        async prev(context) {
          context.commit('_startLoad');
          const response = await client.get<PaginatedResponse<Consignment>>(context.state.prevLink);
          context.commit('_update', response.data);
        },
        async next(context) {
          context.commit('_startLoad');
          const response = await client.get<PaginatedResponse<Consignment>>(context.state.nextLink);
          context.commit('_update', response.data);
        },
        async markReviewed(context, consignment: UnreviewedConsignment) {
          const response = await client.post<ReviewResponse>(consignment.review_url);
          context.commit('_updateConsignment', {
            ...consignment,
            ...response.data,
            reviewed: true,
          });
        },
        async markUnreviewed(context, consignment: ReviewedConsignment) {
          const response = await client.post<UnreviewResponse>(consignment.unreview_url);
          context.commit('_updateConsignment', {
            ...consignment,
            ...response.data,
            reviewed: false,
          });
        },
        async recordIncident(context, { consignment, incidentTypeId }: { consignment: BaseConsignment; incidentTypeId: number }) {
          const response = await client.post<BaseConsignment>(
            consignment.incident_type_url,
            { incidentTypeId },
          );
          context.commit('_updateConsignment', {
            ...consignment,
            ...response.data,
          });
        },
        async deleteIncident(context, consignment: BaseConsignment) {
          const response = await client.delete<BaseConsignment>(consignment.incident_type_url);
          context.commit('_updateConsignment', {
            ...consignment,
            ...response.data,
          });
        },
        async exportToExcel(context, filters?: FilterSpec): Promise<Blob> {
          const response = await client.get('api/consignment/excel/', {
            params: filterSpecToGetParams(filters),
            responseType: 'blob',
            headers: {
              accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
          });
          console.log(response.data);
          return response.data;
        },
      },
    },
    consignmentStatuses: {
      namespaced: true,
      state: {
        consignmentStatuses: [] as ConsignmentStatus[],
      },
      getters: {
        all(state) {
          return state.consignmentStatuses;
        },
      },
      mutations: {
        _set(state, statuses: ConsignmentStatus[]) {
          state.consignmentStatuses = statuses;
        },
      },
      actions: {
        async load(context) {
          const response = await client.get<ConsignmentStatus[]>('api/consignment-status/');
          context.commit('_set', response.data);
        },
      },
    },
    accounts: {
      namespaced: true,
      state: {
        accounts: [] as Account[],
      },
      getters: {
        all(state) {
          return state.accounts;
        },
      },
      mutations: {
        _set(state, accounts: Account[]) {
          state.accounts = accounts;
        },
      },
      actions: {
        async load(context) {
          const response = await client.get<Account[]>('api/account/');
          context.commit('_set', response.data);
        },
      },
    },
    services: {
      namespaced: true,
      state: {
        services: [] as Service[],
      },
      getters: {
        all(state) {
          return state.services;
        },
      },
      mutations: {
        _set(state, services: Depot[]) {
          state.services = services;
        },
      },
      actions: {
        async load(context) {
          const response = await client.get<Service[]>('api/service/');
          context.commit('_set', response.data);
        },
      },
    },
    depots: {
      namespaced: true,
      state: {
        depots: [] as Depot[],
      },
      getters: {
        all(state) {
          return state.depots;
        },
      },
      mutations: {
        _set(state, depots: Depot[]) {
          state.depots = depots;
        },
      },
      actions: {
        async load(context) {
          const response = await client.get<Depot[]>('api/depot/');
          context.commit('_set', response.data);
        },
      },
    },
    incidentTypes: {
      namespaced: true,
      state: {
        incidentTypes: [] as IncidentType[],
      },
      getters: {
        all(state) {
          return state.incidentTypes;
        },
      },
      mutations: {
        _set(state, incidentTypes: IncidentType[]) {
          state.incidentTypes = incidentTypes;
        },
      },
      actions: {
        async load(context) {
          const response = await client.get<IncidentType[]>('api/incident-type/');
          context.commit('_set', response.data);
        },
      },
    },
  },
});

const defaultAdapter = client.defaults.adapter;

client.defaults.adapter = async(config) => {
  if (defaultAdapter === undefined) {
    throw new Error('No default adapter configured for Axios');
  }
  try {
    return await defaultAdapter(config);
  } catch (e) {
    if (config.url === 'aaa/token/refresh/') {
      return e.response;
    }
    switch (e.response.status) {
      case 401:
        try {
          await store.dispatch('refreshJWT');
          config.headers.Authorization = `Bearer ${store.state.jwtAccess}`;
          return await defaultAdapter(config);
        } catch {
          store.commit('clearJwt');
          store.state.authFailureHandler(AUTH_FAILURE_TYPE.UNAUTHORISED);
          return e.response;
        }
      case 403:
        store.commit('clearJwt');
        store.state.authFailureHandler(AUTH_FAILURE_TYPE.FORBIDDEN);
        return e.response;
      default:
        return e.response;
    }
  }
};

export default store;
